/**
 * Contribution Summary Component
 *
 * Displays aggregated totals for all employees in a submission.
 * Shows wages, contributions breakdown, and grand total.
 */

import { SubmissionSummary } from '@/types/nib/manualEntry';
import { formatCurrency } from '@/services/utils/formatters';

interface ContributionSummaryProps {
  summary: SubmissionSummary;
  isHospitality: boolean;
  isLoading?: boolean;
}

export function ContributionSummary({
  summary,
  isHospitality,
  isLoading = false,
}: ContributionSummaryProps) {
  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            Submission Summary
          </h3>
          <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
            <span className="text-sm font-medium text-white">
              {summary.total_employees ?? 0} Employee
              {(summary.total_employees ?? 0) !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Wages Section */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Total Wages</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Basic Wages</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(summary.total_basic_wages ?? 0)}
            </span>
          </div>
          {isHospitality && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Gratuity/Tips</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(summary.total_gratuity_wages ?? 0)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Contributions Section */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Total Contributions
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Employee (EE)</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(summary.total_employee_contributions ?? 0)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Employer (ER)</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(summary.total_employer_contributions ?? 0)}
            </span>
          </div>
          {isHospitality && (summary.total_gratuity_contributions ?? 0) > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Gratuity</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(summary.total_gratuity_contributions ?? 0)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Grand Total */}
      <div className="px-6 py-4 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-base font-semibold text-gray-900">
              Grand Total
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              Total NIB contributions due
            </p>
          </div>
          <span className="text-2xl font-bold text-blue-600">
            {formatCurrency(summary.grand_total ?? 0)}
          </span>
        </div>
      </div>

      {/* Additional Info */}
      {(summary.total_employees ?? 0) === 0 && (
        <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-100">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-800">
                No employees added yet
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Add employees to calculate contributions for this submission.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContributionSummary;
